import { mdiCheck, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState } from 'react';
import { Popover, Spinner } from '../base';
import { MessagingPill } from './messaging-badge';
import * as S from './message-fields/message-fields-redesigned.styles';

export interface ICategoryOption {
    value: string;
    label: string;
}

export interface ICategoryPickerProps {
    selectedIds: string[];
    options: ICategoryOption[];
    loading?: boolean;
    saving?: boolean;
    disabled?: boolean;
    onToggle: (categoryId: string) => void | Promise<void>;
}

export const CategoryPicker: React.FC<ICategoryPickerProps> = ({
    selectedIds,
    options,
    loading = false,
    saving = false,
    disabled = false,
    onToggle,
}) => {
    const [open, setOpen] = useState(false);
    const busy = loading || saving || disabled;

    const selectedLabels = selectedIds.map(
        id => options.find(o => o.value === id)?.label || id,
    );

    const menu = (
        <S.PopoverOptionsList>
            {(loading || saving) && <Spinner size="16px" />}
            {!loading &&
                !saving &&
                options.map(opt => {
                    const isSelected = selectedIds.includes(opt.value);
                    return (
                        <S.PopoverOption
                            key={opt.value}
                            selected={isSelected}
                            onClick={() => {
                                if (!busy) {
                                    void onToggle(opt.value);
                                }
                            }}>
                            <S.PopoverOptionIcon>
                                {isSelected && <Icon path={mdiCheck} size={0.6} />}
                            </S.PopoverOptionIcon>
                            {opt.label}
                        </S.PopoverOption>
                    );
                })}
            {options.length === 0 && !loading && !saving && (
                <S.PopoverOption>No categories</S.PopoverOption>
            )}
        </S.PopoverOptionsList>
    );

    if (selectedIds.length === 0) {
        return (
            <Popover
                open={open}
                onOpenChange={next => {
                    if (!disabled) {
                        setOpen(next);
                    }
                }}
                align="right"
                verticalAlign="bottom"
                renderButton={() => (
                    <S.EmptyFieldTrigger type="button" disabled={busy} aria-label="Category">
                        <Icon path={mdiPlus} size={0.7} />
                        Category
                    </S.EmptyFieldTrigger>
                )}>
                {menu}
            </Popover>
        );
    }

    return (
        <Popover
            open={open}
            onOpenChange={next => {
                if (!disabled) {
                    setOpen(next);
                }
            }}
            align="right"
            verticalAlign="bottom"
            renderButton={() => (
                <S.TagsList
                    role="button"
                    tabIndex={busy ? -1 : 0}
                    aria-label="Change categories"
                    aria-expanded={open}
                    aria-disabled={busy || undefined}
                    onKeyDown={event => {
                        if (busy || disabled) {
                            return;
                        }
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            setOpen(!open);
                        }
                    }}>
                    {selectedIds.map((categoryId, i) => (
                        <MessagingPill key={categoryId} title="Category">
                            {selectedLabels[i] ?? categoryId}
                        </MessagingPill>
                    ))}
                </S.TagsList>
            )}>
            {menu}
        </Popover>
    );
};
